import { toast } from 'react-toastify';
import { createApi } from '@reduxjs/toolkit/query/react';
import { HTTP_METHODS } from 'shared/consts';
import { ErrorMessageDataType } from 'shared/types';
import { baseQueryWithReauth } from '../../shared/store';
import {
  IGetAdminAwards,
  IGetAdminCompetition,
  IGetAdminCompetitionStats,
  IGetCompetitions,
  MGetAdminCompetitionItemAwards,
  MGetAdminCompetitionItemParticipants,
  MGetAdminCompetitionItemRatingHistory,
  MGetAdminCompetitionItemReport,
  MGetAdminCompetitionPrizePlace,
  MGetAdminCompetitionPrizePlaces,
  MGetAdminCompetitionsReport,
  MGetCompetitionSalePoint,
  MPatchDeliveryInfo,
  MRejectAward,
  MSendAward,
} from './types';

const competitionsB2CRApi = createApi({
  reducerPath: 'competitionsB2CRApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'allCompetitionsB2CR',
    'competitionAwardPlaces',
    'competitionB2CR',
    'competitionB2CRParticipants',
    'competitionStats',
    'competitionAwards',
    'competitionParticipants',
    'competitionRatingHistory',
    'competitionRatingPrizePlaces',
    'competitionRatingPrizePlace',
    'competitionSalePoint',
    'KPIList',
    'BrandPlansList',
  ],
  endpoints: (builder) => ({
    getCompetitions: builder.query<IGetCompetitions.Response, IGetCompetitions.Params>({
      query: ({ page, size, search }) =>
        `/competitions/admin/competition/?app_type=b2cr&page=${page}&size=${size}&order_by=created_at&asc=false&search=${search}`,
      providesTags: (result, error) => {
        const errorMessage = 'Возникла непредвиденная ошибка. Пожалуйста, попробуйте еще раз.';
        if (error) {
          if (error.status === 500) {
            toast.error(errorMessage.toString(), {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.error((error.data as ErrorMessageDataType).detail, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        return ['allCompetitionsB2CR'];
      },
    }),
    getAdminAwards: builder.query<IGetAdminAwards.Response, IGetAdminAwards.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}/award`,
      providesTags: (result, error) => {
        const errorMessage = 'Возникла ошибка при получении призовых мест';
        if (error) {
          if (error.status === 500) {
            toast.error(errorMessage.toString(), {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.error((error.data as ErrorMessageDataType).detail, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        return ['competitionAwardPlaces'];
      },
    }),
    getAdminCompetitionItem: builder.query<IGetAdminCompetition.Response, IGetAdminCompetition.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}`,
      providesTags: (result, error) => {
        const errorMessage = 'Возникла непредвиденная ошибка. Пожалуйста, попробуйте еще раз.';
        if (error) {
          if (error.status === 500) {
            toast.error(errorMessage.toString(), {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.error((error.data as ErrorMessageDataType).detail, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        return ['competitionB2CR'];
      },
    }),
    getAdminCompetitionItemStats: builder.query<IGetAdminCompetitionStats.Response, IGetAdminCompetitionStats.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}/participant/purchase_amount`,
      providesTags: ['competitionStats'],
    }),
    getAdminCompetitionItemAwardPlaces: builder.query<
      MGetAdminCompetitionItemAwards.Response,
      MGetAdminCompetitionItemAwards.Params
    >({
      query: () => '/competitions/admin/competition/award_places',
      providesTags: ['competitionAwards'],
    }),
    getAdminCompetitionItemParticipants: builder.query<
      MGetAdminCompetitionItemParticipants.Response | void,
      MGetAdminCompetitionItemParticipants.Params
    >({
      query: ({ competition_id, page, size }) =>
        `/competitions/admin/competition/${competition_id}/participant?page=${page}&size=${size}&order_by=place&asc=true`,
      providesTags: ['competitionParticipants'],
    }),
    getAdminCompetitionItemReport: builder.mutation<
      MGetAdminCompetitionItemReport.Response,
      MGetAdminCompetitionItemReport.Params
    >({
      query: ({ competition_id }) => ({
        url: `/competitions/admin/competition/${competition_id}/report`,
        responseHandler: async (r) => r.blob(),
      }),
    }),
    getAdminCompetitionsReport: builder.mutation<
      MGetAdminCompetitionsReport.Response,
      MGetAdminCompetitionsReport.Params | void
    >({
      query: () => ({
        url: '/competitions/admin/competition/report//by_participant',
        responseHandler: async (r) => r.blob(),
      }),
    }),
    getAdminCompetitionItemRatingHistory: builder.query<
      MGetAdminCompetitionItemRatingHistory.Response,
      MGetAdminCompetitionItemRatingHistory.Params
    >({
      query: ({ competitionId, participantId }) =>
        `/competitions/admin/competition/${competitionId}/participant/${participantId}/rating-history`,
      providesTags: ['competitionRatingHistory'],
    }),
    getAdminCompetitionItemPrizePlace: builder.query<
      MGetAdminCompetitionPrizePlace.Response,
      MGetAdminCompetitionPrizePlace.Params
    >({
      query: ({ award_place_id }) => ({
        url: `/competitions/admin/competition/award_places/${award_place_id}`,
      }),
      providesTags: ['competitionRatingPrizePlace'],
    }),
    getAdminCompetitionItemPrizePlaces: builder.query<
      MGetAdminCompetitionPrizePlaces.Response,
      MGetAdminCompetitionPrizePlaces.Params | void
    >({
      query: (params) => ({
        url: '/competitions/admin/competition/award_places/all/',
        params: params || undefined,
      }),
      providesTags: ['competitionRatingPrizePlaces'],
    }),
    patchAwardDeliveryInfo: builder.mutation<MPatchDeliveryInfo.Response, MPatchDeliveryInfo.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.award_place_id}/delivery-info/`,
        body: {
          ...body,
          award_place_id: undefined,
          id: undefined,
          created_at: undefined,
        },
        method: 'PATCH',
      }),
      invalidatesTags: ['competitionB2CR'],
    }),
    rejectAward: builder.mutation<MRejectAward.Response, MRejectAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/reject/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['competitionB2CR'],
    }),
    sendAward: builder.mutation<MSendAward.Response, MSendAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/award-send/`,
        method: 'POST',
      }),
      invalidatesTags: ['competitionB2CR'],
    }),
    addCoins: builder.mutation<MSendAward.Response, MSendAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/add-coins`,
        method: 'POST',
      }),
      invalidatesTags: ['competitionB2CR'],
    }),
    getCompetitionSalePoint: builder.query<MGetCompetitionSalePoint.Response, MGetCompetitionSalePoint.Params>({
      query: (params) => ({
        url: `/competitions/admin/competition/${params.competition_id}/participant-my/`,
        params,
      }),
      providesTags: ['competitionSalePoint'],
    }),
    deleteParticipantFromCompetition: builder.mutation({
      query: ({ competitionId, participantId }) => ({
        url: `/competitions/admin/competition/${competitionId}/participant/${participantId}/`,
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: (result, error) => {
        const error_message = 'Возникла непредвиденная ошибка. Пожалуйста, попробуйте еще раз.';
        if (error) {
          toast.error(error_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.success('Участник соревнования удалён', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        return [{ type: 'allCompetitionsB2CR' }];
      },
    }),
  }),
});

export { competitionsB2CRApi };
export const {
  useGetCompetitionsQuery,
  useGetAdminAwardsQuery,
  useGetAdminCompetitionItemQuery,
  useGetAdminCompetitionItemStatsQuery,
  useGetAdminCompetitionItemAwardPlacesQuery,
  useGetAdminCompetitionItemParticipantsQuery,
  useGetAdminCompetitionItemReportMutation,
  useGetAdminCompetitionItemRatingHistoryQuery,
  useGetAdminCompetitionItemPrizePlacesQuery,
  useGetAdminCompetitionItemPrizePlaceQuery,
  useGetAdminCompetitionsReportMutation,
  usePatchAwardDeliveryInfoMutation,
  useRejectAwardMutation,
  useSendAwardMutation,
  useAddCoinsMutation,
  useGetCompetitionSalePointQuery,
  useDeleteParticipantFromCompetitionMutation,
} = competitionsB2CRApi;
